import React, { useState } from 'react';
import { Add } from '@mui/icons-material';
import { fetchCards, getMyTransaction, removeCard } from '@/store/Payment';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { AddPaymentCard } from './AddPaymentCardDialog';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import {formatLocalDate} from '../../../utils/FormateLocalDate'
import { capitalizeFirstLetter } from '@/utils/CapitalizeFirstLetter';
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY!);

export const cardLogo = (brand: string) => {
  switch (brand) {
    case 'paypal':
      return '/assets/images/paypal.png';
    case 'visa':
      return '/assets/images/visa.png';
    case 'discover':
      return '/assets/images/discover.png';
    default:
      return '';
  }
};

type BillingData = {
  created_at: string,
  action: string,
  amount: number,
  transaction_type: string,
  source_type: string,
  method: string,
}

type SavedCard = {
  brand: string;
  expMonth: string;
  expYear: string;
  id: string;
  last4: string;
};

const BillingAndPayments = () => {
  const dispatch = useDispatch<any>();
  const [paymentCards, setPaymentCards] = useState<SavedCard[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [billingData, setBillingData] = useState<BillingData[]>([])
  const [balance, setBalance] = useState(0)

  React.useEffect(() => {
    const data = async () => {
      const cardRes = await dispatch(fetchCards());
      setPaymentCards(cardRes.payload.cards);
    };
    data();
  }, [isOpen]);

  React.useEffect(() => {
    const data = async () => {
      const res = await dispatch(getMyTransaction({}));
      setBillingData(res.payload.data[0].transactions);
      setBalance(res.payload.data[0].balance)
    };
    data();
  }, []);

  const removeCardFunction = async (id: string) => {
    const res = await dispatch(removeCard(id));
    if (res.error) {
      toast.error(`❌ Remove Card Failed: ${res.error.message}`);
      return null;
    }
    setPaymentCards((c) => c.filter((c) => c.id !== id));
    toast.success('Remove Card successfully');
  };

  return (
    // make the grid rows flex-fill
    <div className='mx-auto grid w-full h-full auto-rows-fr gap-6 lg:grid-cols-2 lg:gap-8 xl:gap-10 2xl:gap-12'>
      {/* Billing History */}
      <div className='bg-white rounded-[24px] h-full p-6 lg:p-8 xl:p-10 w-full flex flex-col'>
        <h2 className='text-[32px] font-[700] mb-4'>Billing History</h2>
        <div className='flex-1 overflow-y-auto border-b border-gray-200 rounded-b-[24px]'>
          <table className='min-w-full divide-y divide-gray-200 table-fixed'>
            <thead>
              <tr>
                {['Date', 'Method', 'Type', 'Amount', 'Source'].map((h) => (
                  <th
                    key={h}
                    className='sticky top-0 z-10 bg-white px-4 py-2 text-left text-sm font-bold uppercase text-gray-700'
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className='bg-white divide-y divide-gray-200'>
              {billingData && billingData.map((row, i) => (
                <tr key={i} className='hover:bg-gray-50'>
                  <td className='px-4 py-[24px] text-sm text-gray-800'>
                    {formatLocalDate(row.created_at)}
                  </td>
                  <td className='px-4 py-[24px] text-sm text-gray-800'>
                    {capitalizeFirstLetter(row.method)}
                  </td>
                  <td className='px-4 py-[24px] text-sm text-gray-800'>
                    {capitalizeFirstLetter(row.transaction_type)}
                  </td>
                  <td className='px-4 py-[24px] text-sm text-gray-800'>
                    {'$-'+row.amount}
                  </td>
                  <td className='px-4 py-[24px] text-sm text-gray-800'>
                    {capitalizeFirstLetter(row.source_type)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Payment Methods */}
      <div className='bg-white rounded-[24px] h-full p-6 lg:p-8 xl:p-10 w-full flex flex-col'>
        <h2 className='text-[32px] font-[700] mb-4'>Payment Methods</h2>
        <div className='flex-1 overflow-y-auto space-y-4'>
          {paymentCards.length > 0 &&
            paymentCards.map((pc: any) => (
              <div
                key={pc.id}
                className='flex items-center justify-between p-4 rounded-xl border'
                style={
                  // pc.primary
                  //   ?
                  // { background: '#FFF0F2', borderColor: '#FF0050' }
                  // :
                  { background: '#F8F8F8', borderColor: 'lightgreen' }
                }
              >
                <div className='flex items-center'>
                  <div className='w-12 h-12 flex items-center justify-center bg-[#F8F8F8] rounded-md'>
                    <img
                      src={cardLogo(pc.brand)}
                      alt={pc.brand}
                      className='max-w-full max-h-full'
                    />
                  </div>
                  <p className='ml-3 font-normal'>ending in …{pc.last4}</p>
                </div>
                <div className='flex items-center space-x-4'>
                  <button
                    onClick={() => removeCardFunction(pc.id)}
                    className='underline text-red-500'
                  >
                    Remove
                  </button>
                  {/* {!pc.primary && (
                <button className='underline text-teal-400'>
                  Set as primary
                </button>
              )} */}
                </div>
              </div>
            ))}
        </div>

        {/* sits immediately after list */}
        <div className='w-full flex justify-center items-center'>
        <button
          onClick={() => setIsOpen(true)}
          type='button'
          className={`rounded-full w-[250px] flex items-center justify-center
         bg-[#FF0050] text-white py-3
         hover:bg-[#ff0051d7] transition-colors`}
        >
          <Add /> Add New Payment Method
        </button>
        </div>
      </div>
      <Elements stripe={stripePromise}>
        <AddPaymentCard open={isOpen} onClose={() => setIsOpen(false)} />
      </Elements>
    </div>
  );
};

export default BillingAndPayments;
